import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deleteAdminLiveEventsById,
  deleteAdminLiveEventsByIdSessionsBySessionId,
  getAdminLiveEvents,
  getAdminLiveEventsById,
  getAdminLiveEventsByIdLeaderboard,
  getAdminLiveEventsByIdQueue,
  getAdminLiveEventsByIdSessions,
  postAdminLiveEvents,
  putAdminLiveEventsById,
  putAdminLiveEventsByIdScreen,
  putAdminLiveEventsByIdSessionsBySessionIdInvalidate,
  putAdminLiveEventsByIdSessionsBySessionIdRestore,
  type LiveEventAdmin,
  type LiveEventAdminIndexResponse,
  type LiveEventAdminSessionsResponse,
  type LiveEventGameMode,
  type LiveEventGameSessionAdmin,
  type LiveEventInput,
  type LiveEventLeaderboard,
} from 'api/generated'
import { validationMessage } from 'helpers/validation-message'
import { toast } from 'sonner'
import type {
  LiveEventQueueState,
  LiveEventSessionsFilters,
} from 'types/liveEvents'

interface LiveEventsState {
  loading: boolean
  saving: boolean
  error: string | null
  events: LiveEventAdmin[]
  page: number
  totalPages: number
  event: LiveEventAdmin | null
  queue: LiveEventQueueState | null
  sessions: LiveEventGameSessionAdmin[]
  sessionsLoading: boolean
  sessionsTotalPages: number
  leaderboard: LiveEventLeaderboard | null
}

const initialState: LiveEventsState = {
  loading: false,
  saving: false,
  error: null,
  events: [],
  page: 1,
  totalPages: 0,
  event: null,
  queue: null,
  sessions: [],
  sessionsLoading: false,
  sessionsTotalPages: 0,
  leaderboard: null,
}

export const fetchLiveEvents = createAsyncThunk<
  LiveEventAdminIndexResponse,
  number,
  { rejectValue: string }
>('liveEvents/fetchLiveEvents', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(getAdminLiveEvents({ query: { page } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

export const fetchLiveEvent = createAsyncThunk<
  LiveEventAdmin,
  number,
  { rejectValue: string }
>('liveEvents/fetchLiveEvent', async (id, { rejectWithValue }) => {
  try {
    const { live_event } = await unwrap(
      getAdminLiveEventsById({ path: { id } })
    )
    return live_event
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Evento non trovato.'))
  }
})

export const createLiveEvent = createAsyncThunk<
  LiveEventAdmin,
  LiveEventInput,
  { rejectValue: string }
>('liveEvents/createLiveEvent', async (input, { rejectWithValue }) => {
  try {
    const { live_event } = await unwrap(
      postAdminLiveEvents({ body: { live_event: input } })
    )
    toast.success('Evento creato')
    return live_event
  } catch (error) {
    const message = validationMessage(error, 'Salvataggio fallito.')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const updateLiveEvent = createAsyncThunk<
  LiveEventAdmin,
  { id: number; input: LiveEventInput },
  { rejectValue: string }
>('liveEvents/updateLiveEvent', async ({ id, input }, { rejectWithValue }) => {
  try {
    const { live_event } = await unwrap(
      putAdminLiveEventsById({ path: { id }, body: { live_event: input } })
    )
    toast.success('Evento salvato')
    return live_event
  } catch (error) {
    const message = validationMessage(error, 'Salvataggio fallito.')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const deleteLiveEvent = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('liveEvents/deleteLiveEvent', async (id, { rejectWithValue }) => {
  try {
    await unwrap(deleteAdminLiveEventsById({ path: { id } }))
    toast.success('Evento eliminato')
    return id
  } catch (error) {
    toast.error("Impossibile eliminare l'evento.")
    return rejectWithValue(errorMessage(error, 'Eliminazione fallita.'))
  }
})

export const setLiveEventScreen = createAsyncThunk<
  LiveEventAdmin,
  { id: number; enabled: boolean },
  { rejectValue: string }
>('liveEvents/setScreen', async ({ id, enabled }, { rejectWithValue }) => {
  try {
    const { live_event } = await unwrap(
      putAdminLiveEventsByIdScreen({ path: { id }, body: { enabled } })
    )
    toast.success(enabled ? 'Maxischermo acceso' : 'Maxischermo spento')
    return live_event
  } catch (error) {
    toast.error('Impossibile cambiare lo stato del maxischermo.')
    return rejectWithValue(errorMessage(error, 'Operazione fallita.'))
  }
})

export const fetchLiveEventQueue = createAsyncThunk<
  LiveEventQueueState,
  number,
  { rejectValue: string }
>('liveEvents/fetchQueue', async (id, { rejectWithValue }) => {
  try {
    const { success: _success, ...queue } = await unwrap(
      getAdminLiveEventsByIdQueue({ path: { id } })
    )
    return queue
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

export const fetchLiveEventSessions = createAsyncThunk<
  LiveEventAdminSessionsResponse,
  { id: number; page: number; filters: LiveEventSessionsFilters },
  { rejectValue: string }
>(
  'liveEvents/fetchSessions',
  async ({ id, page, filters }, { rejectWithValue }) => {
    try {
      return await unwrap(
        getAdminLiveEventsByIdSessions({
          path: { id },
          query: { ...filters, page },
        })
      )
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
    }
  }
)

export const invalidateLiveEventSession = createAsyncThunk<
  LiveEventGameSessionAdmin,
  { id: number; sessionId: number; reason?: string },
  { rejectValue: string }
>(
  'liveEvents/invalidateSession',
  async ({ id, sessionId, reason }, { rejectWithValue }) => {
    try {
      const { session } = await unwrap(
        putAdminLiveEventsByIdSessionsBySessionIdInvalidate({
          path: { id, session_id: sessionId },
          body: { reason },
        })
      )
      toast.success('Partita annullata')
      return session
    } catch (error) {
      toast.error('Impossibile annullare la partita.')
      return rejectWithValue(errorMessage(error, 'Operazione fallita.'))
    }
  }
)

export const restoreLiveEventSession = createAsyncThunk<
  LiveEventGameSessionAdmin,
  { id: number; sessionId: number },
  { rejectValue: string }
>(
  'liveEvents/restoreSession',
  async ({ id, sessionId }, { rejectWithValue }) => {
    try {
      const { session } = await unwrap(
        putAdminLiveEventsByIdSessionsBySessionIdRestore({
          path: { id, session_id: sessionId },
        })
      )
      toast.success('Partita ripristinata')
      return session
    } catch (error) {
      toast.error('Impossibile ripristinare la partita.')
      return rejectWithValue(errorMessage(error, 'Operazione fallita.'))
    }
  }
)

export const deleteLiveEventSession = createAsyncThunk<
  LiveEventGameSessionAdmin,
  { id: number; sessionId: number },
  { rejectValue: string }
>(
  'liveEvents/deleteSession',
  async ({ id, sessionId }, { rejectWithValue }) => {
    try {
      const { session } = await unwrap(
        deleteAdminLiveEventsByIdSessionsBySessionId({
          path: { id, session_id: sessionId },
        })
      )
      toast.success('Partita eliminata')
      return session
    } catch (error) {
      toast.error('Impossibile eliminare la partita.')
      return rejectWithValue(errorMessage(error, 'Operazione fallita.'))
    }
  }
)

export const fetchLiveEventLeaderboard = createAsyncThunk<
  LiveEventLeaderboard,
  { id: number; mode: LiveEventGameMode },
  { rejectValue: string }
>('liveEvents/fetchLeaderboard', async ({ id, mode }, { rejectWithValue }) => {
  try {
    return await unwrap(
      getAdminLiveEventsByIdLeaderboard({ path: { id }, query: { mode } })
    )
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

const replaceSession = (
  state: LiveEventsState,
  session: LiveEventGameSessionAdmin
) => {
  state.sessions = state.sessions.map((s) =>
    s.id === session.id ? session : s
  )
}

const replaceEvent = (state: LiveEventsState, event: LiveEventAdmin) => {
  state.event = event
  state.events = state.events.map((e) => (e.id === event.id ? event : e))
}

const liveEventsSlice = createSlice({
  name: 'liveEvents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveEvents.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLiveEvents.fulfilled, (state, action) => {
        state.loading = false
        state.events = action.payload.data
        state.page = action.payload.page
        state.totalPages = action.payload.total_pages
      })
      .addCase(fetchLiveEvents.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Errore'
        toast.error('Errore nel caricamento degli eventi')
      })
      .addCase(fetchLiveEvent.pending, (state) => {
        state.loading = true
        state.error = null
        state.event = null
        state.queue = null
        state.sessions = []
        state.leaderboard = null
      })
      .addCase(fetchLiveEvent.fulfilled, (state, action) => {
        state.loading = false
        state.event = action.payload
      })
      .addCase(fetchLiveEvent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Errore'
      })
      .addCase(createLiveEvent.pending, (state) => {
        state.saving = true
      })
      .addCase(createLiveEvent.fulfilled, (state, action) => {
        state.saving = false
        state.event = action.payload
      })
      .addCase(createLiveEvent.rejected, (state) => {
        state.saving = false
      })
      .addCase(updateLiveEvent.pending, (state) => {
        state.saving = true
      })
      .addCase(updateLiveEvent.fulfilled, (state, action) => {
        state.saving = false
        replaceEvent(state, action.payload)
      })
      .addCase(updateLiveEvent.rejected, (state) => {
        state.saving = false
      })
      .addCase(deleteLiveEvent.fulfilled, (state, action) => {
        state.events = state.events.filter((e) => e.id !== action.payload)
      })
      .addCase(setLiveEventScreen.fulfilled, (state, action) => {
        replaceEvent(state, action.payload)
      })
      .addCase(fetchLiveEventQueue.fulfilled, (state, action) => {
        state.queue = action.payload
      })
      .addCase(fetchLiveEventSessions.pending, (state) => {
        state.sessionsLoading = true
      })
      .addCase(fetchLiveEventSessions.fulfilled, (state, action) => {
        state.sessionsLoading = false
        state.sessions = action.payload.data
        state.sessionsTotalPages = action.payload.total_pages
      })
      .addCase(fetchLiveEventSessions.rejected, (state) => {
        state.sessionsLoading = false
        toast.error('Errore nel caricamento delle partite')
      })
      .addCase(invalidateLiveEventSession.fulfilled, (state, action) => {
        replaceSession(state, action.payload)
      })
      .addCase(restoreLiveEventSession.fulfilled, (state, action) => {
        replaceSession(state, action.payload)
      })
      .addCase(deleteLiveEventSession.fulfilled, (state, action) => {
        replaceSession(state, action.payload)
      })
      .addCase(fetchLiveEventLeaderboard.fulfilled, (state, action) => {
        state.leaderboard = action.payload
      })
  },
})

export const liveEventsReducer = liveEventsSlice.reducer
